import type { IndividualFinancialBreakdown } from './schemas/loanServiceSchemas/individualFinancialBreakdown';
import { customAxiosInstanceMutator } from './custom-axios-instance';
import type { Financial } from './schemas/loanServiceSchemas/financial';
import type { ComparisonInput } from './individualPricingComparison';
import type { IndividualPricingQuote } from './individualPricingQuotations';

export interface PreviousIndividualFinancials {
  applicationId: string;
  applicationNumber: string;
  financialDetails: Financial;
}

export async function getPreviousIndividualFinancials(customerPartyId: string, signal?: AbortSignal) {
  const result = await customAxiosInstanceMutator<PreviousIndividualFinancials | ''>({
    url: `/loan/api/v1/customers/${encodeURIComponent(customerPartyId)}/financial-details`, method: 'GET', signal,
  });
  return result || null;
}

export interface AffordabilityFinancial {
  grossIncome?: number; netIncome?: number; otherIncome?: number;
  livingExpenses?: number; maintenance?: number; debtPayments?: number;
}
export interface AffordabilityResult {
  stage: 'PRELIMINARY' | 'FINAL'; status: 'INCOMPLETE' | 'AFFORDABLE' | 'UNAFFORDABLE' | 'STALE';
  ruleVersion: string; financial: AffordabilityFinancial; minimumLivingExpenses?: number;
  assessedLivingExpenses?: number; capacity?: number; largestRepayment?: number;
  affordabilityRatio?: number; maximumAmount?: number; reasons?: string[];
  limits?: { maximumCapacityUtilisationPercent: number; minimumPostRepaymentSurplus: number };
  postRepaymentSurplus?: number;
  cashPositions?: { date: string; income: number; essentialPayments: number; repayment: number; remainingCash: number }[];
}
export interface AffordabilityCashFlow {
  throughDate?: string; openingCash?: number;
  receipts?: { date: string; amount?: number; description: string }[];
  payments?: { date: string; amount?: number; description: string }[];
  confirmed: boolean; basis: string;
}
export interface AffordabilityIncomeReview {
  frequency?: 'MONTHLY' | 'WEEKLY' | 'FORTNIGHTLY'; livingExpensesDeducted?: number; maintenanceDeducted?: number; debtDeducted?: number;
  months?: { month: string; grossIncome?: number; netIncome?: number; otherIncome?: number }[];
  latestNormalMonth?: string; exceptionReason?: string;
}
export interface AffordabilityEvidence {
  financial: AffordabilityFinancial; documentIds: string[]; statementFrom: string; statementTo: string;
  variableIncome: boolean; incomeBasis: string; reconciliationNotes: string; declaredBureauOverlap?: number; personalPurpose: boolean; soleIncome: boolean; employmentType?: string;
  cashFlow?: AffordabilityCashFlow;
  incomeReview?: AffordabilityIncomeReview;
  debtMatches?: { bureauReportId: string; bureauFingerprint: string; declarationFingerprint: string; matchedAccountIds: string[]; confirmed: boolean; includedAccountIds?: string[] };
}
export type AffordabilityDebtMatches = NonNullable<AffordabilityEvidence['debtMatches']>;
export interface AffordabilityBureauDebts {
  reportId: string; fingerprint: string; declarationFingerprint: string; retrievedAt: string; declaredDebt?: number; monthlyDebt?: number;
  accounts?: { id: string; lender?: string; accountNumber?: string; accountType?: string; monthlyRepayment: number }[];
  reviewReason?: string;
}
export interface AffordabilitySnapshot {
  result: AffordabilityResult; quotationId: string; pricingVersionId: string;
  evidence?: AffordabilityEvidence; bureauReportId?: string; bureauRetrievedAt?: string;
  reviewedBy?: string; reviewedAt?: string; analystFingerprint?: string;
  captured?: { individualBreakdown?: import('./schemas/loanServiceSchemas/individualFinancialBreakdown').IndividualFinancialBreakdown; financial?: AffordabilityFinancial; otherLivingExpenseItems?: { name: string; amount: number }[] };
}

/** Application DTOs use documentType; retained evidence references use type. */
export const affordabilityDocumentType = (document: any): string | undefined => document.documentType || document.type;

/** Reuse recorded facts only. Amounts and verification confirmations are never inferred. */
export function prefillAffordabilityEvidence(application: any, evidence: AffordabilityEvidence, suggestDocuments = false): AffordabilityEvidence {
  const date = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
  if (suggestDocuments && evidence.documentIds.length === 0) {
    const today = new Date().toISOString().slice(0, 10);
    evidence = { ...evidence, documentIds: (application.documents || []).filter((document: any) =>
      ['BANK_STATEMENT', 'PAYSLIP'].includes(affordabilityDocumentType(document) || '') && document.status !== 'SUPERSEDED'
      && (!date(document.expiryDate) || document.expiryDate >= today)).map((document: any) => document.id) };
  }
  const statements = (application.documents || []).filter((document: any) =>
    affordabilityDocumentType(document) === 'BANK_STATEMENT' && document.status !== 'SUPERSEDED'
    && evidence.documentIds.includes(document.id));
  const months = statements.map((document: any) => document.period);
  const knownMonths = months.length > 0 && months.every((period: unknown) => typeof period === 'string'
    && /^\d{4}-(0[1-9]|1[0-2])$/.test(period));
  const ordered: string[] = knownMonths ? [...new Set<string>(months)].sort() : [];
  const consecutive = ordered.every((month, index) => index === 0 ||
    new Date(Date.UTC(Number(ordered[index - 1].slice(0, 4)), Number(ordered[index - 1].slice(5, 7)), 1)).toISOString().slice(0, 7) === month);
  const last = ordered[ordered.length - 1];
  const statementTo = last ? new Date(Date.UTC(Number(last.slice(0, 4)), Number(last.slice(5, 7)), 0)).toISOString().slice(0, 10) : '';
  return { ...evidence,
    statementFrom: evidence.statementFrom || (consecutive && ordered.length > 0 ? `${ordered[0]}-01` : ''),
    statementTo: evidence.statementTo || (consecutive ? statementTo : '')
  };
}
const BASE = '/loan/api/v1/settings/individual-pricing/affordability';
export const previewIndividualAffordability = (financial: AffordabilityFinancial, pricing: ComparisonInput, version: string, signal?: AbortSignal, debtMatches?: AffordabilityDebtMatches, idType?: string) =>
  customAxiosInstanceMutator<AffordabilityResult>({ url: `${BASE}/preview`, method: 'POST', signal,
    data: { financial, debtMatches, ...(idType === 'PASSPORT' && {idType}), pricing: { ...pricing, expectedVersion: version, expectedRevision: undefined, firstDueMonth: undefined } } });
export const previewIndividualBureauDebts = async (applicantId: string, financial: AffordabilityFinancial, signal?: AbortSignal, idType?: string) => {
  const result = await customAxiosInstanceMutator<AffordabilityBureauDebts | null | ''>({ url: `${BASE}/bureau-debts/preview`, method: 'POST', signal,
    data: { applicantId, financial, ...(idType === 'PASSPORT' && {idType}) } });
  // Spring returns an empty body when no report exists; Axios exposes it as an empty string.
  return result === '' || result == null ? null : result;
};
export const getIndividualAffordability = (id: string) =>
  customAxiosInstanceMutator<AffordabilitySnapshot | null>({ url: `${BASE}/applications/${encodeURIComponent(id)}`, method: 'GET' });
export const getIndividualAffordabilityBureauDebts = (id: string) =>
  customAxiosInstanceMutator<AffordabilityBureauDebts | null>({ url: `${BASE}/applications/${encodeURIComponent(id)}/bureau-debts`, method: 'GET' });
export const verifyIndividualAffordability = (id: string, evidence: AffordabilityEvidence) =>
  customAxiosInstanceMutator<AffordabilitySnapshot>({ url: `${BASE}/applications/${encodeURIComponent(id)}/verify`, method: 'POST', data: evidence });
const amount = (value: unknown): number | undefined => typeof value === 'number' && Number.isFinite(value)
  && value >= 0 && value <= Number.MAX_SAFE_INTEGER / 100
  && Math.abs(value * 100 - Math.round(value * 100)) < 0.000001 ? value : undefined;
/** Missing expense items remain incomplete; sum cents so previews and retained results agree. */
export function sumMonthlyAmounts(values: unknown[]): number | undefined {
  if (values.some(value => amount(value) === undefined)) return undefined;
  const cents = values.reduce<number>((sum, value) => sum + Math.round((value as number) * 100), 0);
  return Number.isSafeInteger(cents) ? cents / 100 : undefined;
}
/** A supplied breakdown owns only the other-cost subtotal; legacy totals remain explicit. */
export function otherLivingExpenseTotal(financial: any): number | undefined {
  const items = financial?.otherLivingExpenseItems;
  if (items == null) return amount(financial?.otherLivingExpenses);
  if (!Array.isArray(items) || items.some(row => !row || typeof row.name !== 'string' || !row.name.trim()
    || row.name.trim().length > 255 || amount(row.amount) === undefined)) return undefined;
  const total = sumMonthlyAmounts(items.map(row => row.amount));
  return total !== undefined && total <= 9999999999999.99 ? total : undefined;
}
/** The demonstrated monthly capture catalogue; the same record is reused at assessment. */
export const individualBreakdownGroups: { title: string; fields: { name: keyof IndividualFinancialBreakdown; label: string }[] }[] = [
  {
    "title": "Gross Income",
    "fields": [
      {
        "name": "bonus",
        "label": "Bonus"
      },
      {
        "name": "commission",
        "label": "Commission"
      },
      {
        "name": "governmentGrant",
        "label": "Government Grant"
      },
      {
        "name": "otherGrossIncome",
        "label": "Other"
      },
      {
        "name": "pensionIncome",
        "label": "Pension"
      },
      {
        "name": "salaryWages",
        "label": "Salary Wages"
      }
    ]
  },
  {
    "title": "Statutory / Pay slip Deductions",
    "fields": [
      {
        "name": "medicalDeduction",
        "label": "Medical"
      },
      {
        "name": "otherDeduction",
        "label": "Other"
      },
      {
        "name": "pensionDeduction",
        "label": "Pension"
      },
      {
        "name": "taxDeduction",
        "label": "Tax"
      }
    ]
  },
  {
    "title": "Necessary Expenses",
    "fields": [
      {
        "name": "cellphone",
        "label": "Cellphone"
      },
      {
        "name": "childCare",
        "label": "Child Care"
      },
      {
        "name": "foodGroceries",
        "label": "Food & Groceries"
      },
      {
        "name": "fuelCost",
        "label": "Fuel Cost"
      },
      {
        "name": "housing",
        "label": "Housing"
      },
      {
        "name": "otherInsurance",
        "label": "Other Insurance"
      },
      {
        "name": "utility",
        "label": "Utility"
      },
      {
        "name": "vehicle",
        "label": "Vehicle"
      }
    ]
  }
];
export function hasIndividualBreakdown(financial: any): boolean {
  return individualBreakdownGroups.some(group => group.fields.some(field => financial?.individualBreakdown?.[field.name] != null));
}
export function individualBreakdownTotals(financial: any) {
  const totals = individualBreakdownGroups.map(group => sumMonthlyAmounts(group.fields.map(field => financial?.individualBreakdown?.[field.name] ?? 0)));
  const [grossIncome, deductions, necessaryExpenses] = totals;
  const netIncome = grossIncome !== undefined && deductions !== undefined && deductions <= grossIncome
    ? Math.round((grossIncome - deductions) * 100) / 100 : undefined;
  return { grossIncome, deductions, netIncome, necessaryExpenses };
}
export function individualBreakdownIssues(financial: any): { name: string; message: string }[] {
  if (!hasIndividualBreakdown(financial) && (financial?.affordabilityCaptured || financial?.individualBreakdown == null)) return [];
  const issues = individualBreakdownGroups.flatMap(group => group.fields
    .filter(field => amount(financial.individualBreakdown[field.name] ?? 0) === undefined)
    .map(field => ({ name: field.name, message: `Enter a non-negative ${field.label} amount to cents, or leave it blank for zero.` })));
  const totals = individualBreakdownTotals(financial);
  if (totals.grossIncome != null && totals.deductions != null && totals.deductions > totals.grossIncome)
    issues.push({ name: 'taxDeduction', message: 'Payslip deductions cannot exceed gross income.' });
  return issues;
}
export function capturedAffordability(values: any): AffordabilityFinancial {
  const f = values?.financial || {};
  const recordedNet = amount(values?.individual?.monthlyNetIncome ?? f.income?.netIncome);
  if (hasIndividualBreakdown(f)) {
    const totals = individualBreakdownTotals(f);
    const needsGross = (recordedNet ?? 0) > 0
      && individualBreakdownGroups[0].fields.every(field => f.individualBreakdown[field.name] == null);
    return { grossIncome: needsGross ? undefined : totals.grossIncome,
      netIncome: needsGross ? undefined : totals.netIncome, otherIncome: 0,
      livingExpenses: sumMonthlyAmounts([totals.necessaryExpenses,
        f.otherLivingExpenseItems == null && f.otherLivingExpenses == null ? 0 : otherLivingExpenseTotal(f)]),
      maintenance: amount(f.maintenance ?? 0), debtPayments: sumMonthlyAmounts([f.expenses?.bondInstalment, f.expenses?.vehicleFinance,
        f.expenses?.personalLoan, f.expenses?.creditCard, f.household?.storeCards].map(value => value ?? 0)) };
  }
  return { grossIncome: amount(f.income?.grossIncome), netIncome: recordedNet,
    otherIncome: amount(f.income?.otherIncome),
    livingExpenses: sumMonthlyAmounts([f.expenses?.rent, f.household?.telkomCell, f.household?.education,
      f.household?.ratesAndTaxes, f.household?.groceries, f.household?.fuelTravel, f.household?.insurance, otherLivingExpenseTotal(f)]),
    maintenance: amount(f.maintenance), debtPayments: sumMonthlyAmounts([f.expenses?.bondInstalment, f.expenses?.vehicleFinance,
      f.expenses?.personalLoan, f.expenses?.creditCard, f.household?.storeCards]) };
}

export function individualGrossIncomeIssue(financial: AffordabilityFinancial): string | undefined {
  const { grossIncome, netIncome, otherIncome } = financial;
  const totalNet = sumMonthlyAmounts([netIncome ?? 0, otherIncome ?? 0]) ?? 0;
  if (grossIncome == null || (grossIncome === 0 && totalNet > 0)) {
    return 'Enter gross income before deductions in Income and Expenses. It is used to calculate minimum living expenses.';
  }
  if (totalNet > grossIncome) return 'Review Income and Expenses. Total take-home income cannot exceed gross income.';
  return undefined;
}
/** Materialise blank predefined categories only at submission; draft inputs stay blank. */
export function individualFinancialPayload(financial: any) {
  if (!hasIndividualBreakdown(financial)) return financial;
  const breakdown = Object.fromEntries(individualBreakdownGroups.flatMap(group => group.fields)
    .map(field => [field.name, financial.individualBreakdown[field.name] ?? 0]));
  return { ...financial, individualBreakdown: breakdown,
    maintenance: financial.maintenance ?? 0,
    otherLivingExpenses: financial.otherLivingExpenseItems == null ? financial.otherLivingExpenses ?? 0 : otherLivingExpenseTotal(financial),
    expenses: { ...financial.expenses, ...Object.fromEntries(['bondInstalment','vehicleFinance','personalLoan','creditCard']
      .map(name => [name, financial.expenses?.[name] ?? 0])) },
    household: { ...financial.household, storeCards: financial.household?.storeCards ?? 0 } };
}
export function retainedAffordabilityInput(quote: IndividualPricingQuote): ComparisonInput {
  return { ...quote.inputs, grossAdvance: String(quote.inputs.grossAdvance), expectedRevision: quote.pricing.policy.revision };
}
export function affordabilityError(error: any): string {
  return error?.response?.data?.message || (error?.response?.status === 409
    ? 'Pricing changed. Reload activated pricing and reassess.' : 'Affordability could not be calculated. Retry; this result cannot authorize approval.');
}

/** Changed request inputs must recalculate even while the prior retained quote becomes invalid. */
export function currentAffordabilityInput(loan: any): ComparisonInput | undefined {
  const basis = loan?.individualPricingInputs || (loan?.individualPricingQuote && retainedAffordabilityInput(loan.individualPricingQuote));
  if (!basis) return undefined;
  return { ...basis, grossAdvance: String(loan.loanAmount ?? ''), advanceDate: loan.commencementDate,
    instalments: Number(loan.numberOfInstallments), paydaySchedule: basis.paydaySchedule
      ? { ...basis.paydaySchedule, advanceDate: loan.commencementDate, instalments: Number(loan.numberOfInstallments) } : undefined };
}

export const requoteIndividualApplication = (id: string, quotationId: string) =>
  customAxiosInstanceMutator<AffordabilitySnapshot>({ url: `${BASE}/applications/${encodeURIComponent(id)}/requote`, method: 'POST', data: { quotationId } });
export const reconfirmIndividualAssessment = (id: string, rationale: string) =>
  customAxiosInstanceMutator<AffordabilitySnapshot>({ url: `${BASE}/applications/${encodeURIComponent(id)}/reconfirm`, method: 'POST', data: { rationale } });
